/**
 * The school platform: teacher analytics and school administration.
 *
 * Two rules the callers of this module depend on:
 *
 * - **Nothing polls.** `student()` and `groupRoster()` each write a row to
 *   `student_access_log` — the record of which adult opened a child's card. Call
 *   them on navigation and on an explicit refresh, never on an interval.
 * - **404 is not 403.** A group or student that belongs to another school answers
 *   404, identical to one that does not exist. Render both as "not found".
 */

import type { ApiClient } from '../core/client'
import type { UUID } from '../types/common'
import type {
  CheckoutSession,
  CoverageSummary,
  CoverageTree,
  CoverageUpdateItem,
  FormClass,
  GroupAnalytics,
  GroupSummary,
  Member,
  MemberList,
  MemberRole,
  MemberStatus,
  RosterImportResult,
  RosterRow,
  RosterRowInput,
  School,
  SchoolCurriculum,
  SchoolUpdate,
  SchoolUsage,
  SchoolYear,
  StudentDetail,
  StudentTopicMastery,
  TeacherOverview,
  TeachingGroup,
} from '../types/school'

export class TeacherResource {
  constructor(private readonly client: ApiClient) {}

  overview(signal?: AbortSignal): Promise<TeacherOverview> {
    return this.client.get<TeacherOverview>('/teacher/overview', undefined, signal)
  }

  groups(signal?: AbortSignal): Promise<GroupSummary[]> {
    return this.client.get<GroupSummary[]>('/teacher/groups', undefined, signal)
  }

  /** Pre-sorted server-side: at-risk first, then ascending mastery. Do not
   *  re-sort to alphabetical — the order is the point. */
  groupRoster(groupId: UUID, signal?: AbortSignal): Promise<RosterRow[]> {
    return this.client.get<RosterRow[]>(`/teacher/groups/${groupId}/roster`, undefined, signal)
  }

  groupAnalytics(groupId: UUID, signal?: AbortSignal): Promise<GroupAnalytics> {
    return this.client.get<GroupAnalytics>(`/teacher/groups/${groupId}/analytics`, undefined, signal)
  }

  coverage(groupId: UUID, signal?: AbortSignal): Promise<CoverageTree> {
    return this.client.get<CoverageTree>(`/teacher/groups/${groupId}/coverage`, undefined, signal)
  }

  /** The only teacher-facing write. Marking a topic covered lets the scheduler start
   *  planning it for every student in the group, from their next plan onward. Batched:
   *  a teacher ticking down a unit produces one request, not one per topic. */
  updateCoverage(groupId: UUID, items: CoverageUpdateItem[]): Promise<CoverageSummary> {
    return this.client.put<CoverageSummary>(`/teacher/groups/${groupId}/coverage`, { items })
  }

  student(studentId: UUID, signal?: AbortSignal): Promise<StudentDetail> {
    return this.client.get<StudentDetail>(`/teacher/students/${studentId}`, undefined, signal)
  }

  studentTopics(studentId: UUID, signal?: AbortSignal): Promise<StudentTopicMastery[]> {
    return this.client.get<StudentTopicMastery[]>(
      `/teacher/students/${studentId}/topics`,
      undefined,
      signal,
    )
  }
}

// A type alias rather than an interface on purpose: only aliases get an implicit
// index signature, which is what lets this be passed straight through as a query.
export type MemberQuery = {
  role?: MemberRole
  member_status?: MemberStatus
  year_id?: UUID
  form_class_id?: UUID
  /** Free-text search over name and email. */
  q?: string
  limit?: number
  offset?: number
}


export class SchoolAdminResource {
  constructor(private readonly client: ApiClient) {}

  get(schoolId: UUID, signal?: AbortSignal): Promise<School> {
    return this.client.get<School>(`/schools/${schoolId}`, undefined, signal)
  }

  update(schoolId: UUID, body: SchoolUpdate): Promise<School> {
    return this.client.patch<School>(`/schools/${schoolId}`, body)
  }

  /** The programmes this school runs. This list is exactly what its students see in
   *  the app's curriculum picker, and nothing else. */
  curriculums(schoolId: UUID, signal?: AbortSignal): Promise<SchoolCurriculum[]> {
    return this.client.get<SchoolCurriculum[]>(`/schools/${schoolId}/curriculums`, undefined, signal)
  }

  addCurriculum(schoolId: UUID, curriculumId: UUID, isDefault = false): Promise<SchoolCurriculum> {
    return this.client.post<SchoolCurriculum>(`/schools/${schoolId}/curriculums`, {
      curriculumId,
      isDefault,
    })
  }

  removeCurriculum(schoolId: UUID, curriculumId: UUID): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/curriculums/${curriculumId}`)
  }

  // -- structure -------------------------------------------------------------

  years(schoolId: UUID, signal?: AbortSignal): Promise<SchoolYear[]> {
    return this.client.get<SchoolYear[]>(`/schools/${schoolId}/years`, undefined, signal)
  }

  createYear(schoolId: UUID, body: Partial<SchoolYear>): Promise<SchoolYear> {
    return this.client.post<SchoolYear>(`/schools/${schoolId}/years`, body)
  }

  updateYear(schoolId: UUID, yearId: UUID, body: Partial<SchoolYear>): Promise<SchoolYear> {
    return this.client.patch<SchoolYear>(`/schools/${schoolId}/years/${yearId}`, body)
  }

  deleteYear(schoolId: UUID, yearId: UUID): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/years/${yearId}`)
  }

  formClasses(schoolId: UUID, signal?: AbortSignal): Promise<FormClass[]> {
    return this.client.get<FormClass[]>(`/schools/${schoolId}/form-classes`, undefined, signal)
  }

  createFormClass(schoolId: UUID, body: Partial<FormClass>): Promise<FormClass> {
    return this.client.post<FormClass>(`/schools/${schoolId}/form-classes`, body)
  }

  updateFormClass(schoolId: UUID, id: UUID, body: Partial<FormClass>): Promise<FormClass> {
    return this.client.patch<FormClass>(`/schools/${schoolId}/form-classes/${id}`, body)
  }

  deleteFormClass(schoolId: UUID, id: UUID): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/form-classes/${id}`)
  }

  teachingGroups(
    schoolId: UUID,
    params?: { includeArchived?: boolean },
    signal?: AbortSignal,
  ): Promise<TeachingGroup[]> {
    return this.client.get<TeachingGroup[]>(
      `/schools/${schoolId}/teaching-groups`,
      params ? { include_archived: params.includeArchived } : undefined,
      signal,
    )
  }

  createTeachingGroup(schoolId: UUID, body: Partial<TeachingGroup>): Promise<TeachingGroup> {
    return this.client.post<TeachingGroup>(`/schools/${schoolId}/teaching-groups`, body)
  }

  /** Archiving keeps enrolments, coverage and history. Deleting cascades them —
   *  which is why the two are separate calls and the UI must not conflate them. */
  updateTeachingGroup(
    schoolId: UUID,
    groupId: UUID,
    body: Partial<TeachingGroup>,
  ): Promise<TeachingGroup> {
    return this.client.patch<TeachingGroup>(`/schools/${schoolId}/teaching-groups/${groupId}`, body)
  }

  deleteTeachingGroup(schoolId: UUID, groupId: UUID): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/teaching-groups/${groupId}`)
  }

  assignTeacher(schoolId: UUID, groupId: UUID, teacherId: UUID): Promise<unknown> {
    return this.client.post<unknown>(`/schools/${schoolId}/teaching-groups/${groupId}/teachers`, {
      teacherId,
    })
  }

  unassignTeacher(schoolId: UUID, groupId: UUID, teacherId: UUID): Promise<void> {
    return this.client.delete<void>(
      `/schools/${schoolId}/teaching-groups/${groupId}/teachers/${teacherId}`,
    )
  }

  /** Enrolment takes **membership** ids, not user ids: an invited student has no
   *  profile to point at yet. */
  enrolStudents(schoolId: UUID, groupId: UUID, memberIds: UUID[]): Promise<unknown> {
    return this.client.post<unknown>(`/schools/${schoolId}/teaching-groups/${groupId}/students`, {
      memberIds,
    })
  }

  unenrolStudents(schoolId: UUID, groupId: UUID, memberIds: UUID[]): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/teaching-groups/${groupId}/students`, {
      body: { memberIds },
    })
  }

  // -- roster ----------------------------------------------------------------

  members(schoolId: UUID, query?: MemberQuery, signal?: AbortSignal): Promise<MemberList> {
    return this.client.get<MemberList>(`/schools/${schoolId}/members`, query, signal)
  }

  /** Staff only. Students come in through `importRoster` — teachers and admins are
   *  invite-only by design. */
  inviteStaff(
    schoolId: UUID,
    body: { email: string; role?: 'teacher' | 'admin'; displayName?: string },
  ): Promise<Member> {
    return this.client.post<Member>(`/schools/${schoolId}/invites`, body)
  }

  /**
   * Accepts structured rows or raw CSV — a school has a spreadsheet, not JSON.
   *
   * The result is a *partial-success* report: a 200 with a non-empty `errors` array
   * is the normal outcome. `updateExisting` is off by default because an import that
   * silently rewrites roster rows is a good way to lose a year group's data to a
   * stale file.
   */
  importRoster(
    schoolId: UUID,
    body: { members?: RosterRowInput[]; csv?: string; updateExisting?: boolean },
  ): Promise<RosterImportResult> {
    return this.client.post<RosterImportResult>(`/schools/${schoolId}/members:import`, body)
  }

  /** Disables access and deletes nothing: the student reverts to a personal account
   *  with their history intact. */
  removeMember(schoolId: UUID, memberId: UUID): Promise<void> {
    return this.client.delete<void>(`/schools/${schoolId}/members/${memberId}`)
  }

  /** The school's answer for one student. Null clears it back to "nobody has
   *  decided", which is a distinct state from refusing. */
  setMemberLeaderboardConsent(
    schoolId: UUID,
    memberId: UUID,
    optIn: boolean | null,
  ): Promise<unknown> {
    return this.client.put<unknown>(
      `/schools/${schoolId}/members/${memberId}/leaderboard-consent`,
      { optIn },
    )
  }

  // -- billing ---------------------------------------------------------------

  usage(schoolId: UUID, days?: number, signal?: AbortSignal): Promise<SchoolUsage> {
    return this.client.get<SchoolUsage>(`/schools/${schoolId}/usage`, { days }, signal)
  }

  checkout(schoolId: UUID, seats: number, planCode = 'school_seat'): Promise<CheckoutSession> {
    return this.client.post<CheckoutSession>(`/schools/${schoolId}/billing/checkout-session`, {
      planCode,
      seats,
    })
  }
}
